import React from 'react';
import { useSelector } from 'react-redux';

import { PosPlugin } from 'plugins/plugin';
import { addError } from 'actions/Error';

import { fiscalIntegrations } from './integrations/FiscalIntegration';
import ComponentConfiguration from './ComponentConfiguration';
import {
  EFSTAConf,
  EFSTAConfCompany,
  EFSTAConfPos,
  EFSTAConfUser,
  EFSTAConfWarehouse,
} from './types/pluginTypes';
import { getFiscalIntegrationForSelectedCountry } from './API/selectors';
import { EFSTA_ID, EFSTA_INFO } from './meta';

/**
 * European Fiscal STandards Association plugin
 *
 * @see https://public.efsta.net/efr/DE/EFR_Guide_%5BDE%5D_2.1.6_en.pdf DE Guide
 * @see http://public.efsta.net/efr/DE/collection_business_cases.pdf Example requests
 * @see http://files.ctk.at/AFS/EFSTA%20%28RKS%29/EFR_Installation/EFR_Installation/Sonstiges/EFR_Reference.pdf Types
 */
const EFSTAFiscalIntegration: PosPlugin<
  EFSTAConf,
  EFSTAConfCompany,
  EFSTAConfWarehouse,
  EFSTAConfPos,
  EFSTAConfUser
> = {
  /* Metadata */
  id: EFSTA_ID,
  name: 'EFSTA Fiscal Integration',
  keywords: ['EFSTA', 'fiscal', 'integration'],
  info: EFSTA_INFO,

  /* Config */
  ComponentConfiguration,
  /* Integration hooks */
  getStatus: state => {
    const fi = getFiscalIntegrationForSelectedCountry(state);
    return (
      fi?.getStatus?.(state) ?? {
        type: 'warning',
        message:
          'Selected country has no automatic config validation. POS cannot determine if configuration is valid',
      }
    );
  },
  UICustomTableBillRow: props => {
    const { UICustomTableBillRow: Comp } = useSelector(
      getFiscalIntegrationForSelectedCountry,
    );
    const Component = React.useMemo(() => {
      if (Comp === undefined) return ({ children }) => children;
      return props => Comp(props);
    }, [Comp]);
    const { children = null } = props;
    return <Component {...props}>{children}</Component>;
  },
  combineConfiguration: (c, w, p, u) => ({
    ...(c ?? {}),
    country: c?.country ?? '-',
    endpoint: p?.endpoint,
  }),
  ComponentHeader: ({ children }) => {
    const { ComponentHeader: Comp } = useSelector(
      getFiscalIntegrationForSelectedCountry,
    );

    return (
      <>
        {children}
        {/*
         // @ts-ignore */}
        {Comp && <Comp />}
      </>
    );
  },
  onOpenPaymentModal: {
    on: (p, ap) => async (dispatch, getState) => {
      const { onOpenPaymentModal } = getFiscalIntegrationForSelectedCountry(
        getState(),
      );
      await dispatch(onOpenPaymentModal(p, ap)).catch(err => {
        dispatch(addError(err.message));
        throw err;
      });
      return ap;
    },
  },
  onSaveSalesDocument: {
    on: (props, requests) => async (dispatch, getState) => {
      const { onSaveDocument } = getFiscalIntegrationForSelectedCountry(
        getState(),
      );
      return dispatch(onSaveDocument(requests)).catch(err => {
        dispatch(addError(err.message));
        throw new Error('EFSTA integration failed to register sales document', {
          cause: err,
        });
      });
    },
    after: (props, { requests }) => async (dispatch, getState) => {
      const { afterSaveDocument } = getFiscalIntegrationForSelectedCountry(
        getState(),
      );
      if (!afterSaveDocument) return;

      await dispatch(afterSaveDocument(requests));
    },
  },
  onTryCashInOut: {
    on: (props, cashInOutPayload) => async (dispatch, getState) => {
      const { onCashInOut } = getFiscalIntegrationForSelectedCountry(
        getState(),
      );
      if (onCashInOut === undefined) return cashInOutPayload;

      await dispatch(onCashInOut(props.sum)).catch(err => {
        dispatch(addError(err.message));
        throw err;
      });
      return cashInOutPayload;
    },
  },
  onCloseDay: {
    on: (p, ap) => async (dispatch, getState) => {
      const { onCloseDay } = getFiscalIntegrationForSelectedCountry(getState());
      if (onCloseDay === undefined) return ap;

      await dispatch(onCloseDay()).catch(err => {
        dispatch(addError(err.message));
        throw err;
      });
      return ap;
    },
  },
  onStartNewSale: {
    on: (p, ap) => async (dispatch, getState) => {
      const { onStartNewSale } = getFiscalIntegrationForSelectedCountry(
        getState(),
      );
      if (onStartNewSale === undefined) return ap;

      await dispatch(onStartNewSale()).catch(err => {
        dispatch(addError(err.message));
        throw err;
      });
      return ap;
    },
  },
  onPrintReceipt: {
    on: (p, ap) => async (dispatch, getState) => {
      const { onReprintReceipt } = getFiscalIntegrationForSelectedCountry(
        getState(),
      );
      if (onReprintReceipt === undefined) return ap;

      await dispatch(onReprintReceipt(p)).catch(err => {
        dispatch(addError(err.message));
        throw err;
      });
      return ap;
    },
  },
  components: Object.assign(
    {},
    ...Object.values(fiscalIntegrations).map(int => int.components),
  ),
};

export default EFSTAFiscalIntegration;
